import { useState, useEffect, useCallback } from "react";
import {
  FileText, RefreshCw, CheckCircle, AlertTriangle,
  ChevronDown, ChevronUp, Info, TrendingUp,
  DollarSign, Printer, ClipboardCheck, Clock, Pencil, X, Save
} from "lucide-react";
import { supabase } from "../lib/supabase";

const MESES = [
  "Enero","Febrero","Marzo","Abril","Mayo","Junio",
  "Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"
];

function money(v) {
  if (v === null || v === undefined) return "—";
  if (v === 0) return "$0";
  return new Intl.NumberFormat("es-CL", { style: "currency", currency: "CLP", maximumFractionDigits: 0 }).format(v);
}

function parseCLP(str) {
  if (!str) return 0;
  return parseInt(String(str).replace(/[^0-9]/g, ""), 10) || 0;
}

// ── Campo editable inline ────────────────────────────────────
function CampoEditable({ valor, onGuardar, prefijo = "$", sufijo = "", tipo = "monto", label }) {
  const [editando, setEditando] = useState(false);
  const [temp, setTemp] = useState("");
  const [guardando, setGuardando] = useState(false);

  const abrir = () => {
    setTemp(tipo === "pct" ? String(valor) : String(valor || ""));
    setEditando(true);
  };

  const guardar = async () => {
    setGuardando(true);
    const v = tipo === "pct" ? parseFloat(temp) || 0 : parseCLP(temp);
    await onGuardar(v);
    setGuardando(false);
    setEditando(false);
  };

  if (editando) {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <span style={{ fontSize: 12, color: "#94a3b8" }}>{prefijo}</span>
        <input
          autoFocus
          value={temp}
          onChange={e => setTemp(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter") guardar(); if (e.key === "Escape") setEditando(false); }}
          style={{
            width: tipo === "pct" ? 60 : 140, padding: "4px 8px",
            border: "1px solid #29abe2", borderRadius: 6, fontSize: 13,
            fontFamily: "inherit", outline: "none", color: "#0d1117"
          }}
          placeholder={tipo === "pct" ? "1.0" : "0"}
        />
        <span style={{ fontSize: 12, color: "#94a3b8" }}>{sufijo}</span>
        <button onClick={guardar} disabled={guardando} style={{ background: "#29abe2", border: "none", borderRadius: 5, padding: "4px 8px", cursor: "pointer", color: "#fff", display: "flex", alignItems: "center", gap: 4, fontSize: 12 }}>
          {guardando ? <RefreshCw size={11} style={{ animation: "spin 1s linear infinite" }} /> : <Save size={11} />}
          {guardando ? "" : "OK"}
        </button>
        <button onClick={() => setEditando(false)} style={{ background: "none", border: "1px solid #e2e8f0", borderRadius: 5, padding: "4px 6px", cursor: "pointer", color: "#94a3b8", display: "flex" }}>
          <X size={11} />
        </button>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <span style={{ fontSize: 13, fontWeight: 600, color: valor > 0 ? "#0d1117" : "#94a3b8" }}>
        {tipo === "pct" ? `${valor}%` : money(valor)}
      </span>
      <button onClick={abrir} title={`Editar ${label}`} style={{ background: "none", border: "none", cursor: "pointer", padding: 2, color: "#94a3b8", display: "flex", alignItems: "center" }}>
        <Pencil size={12} />
      </button>
    </div>
  );
}

// ── Línea del formulario ─────────────────────────────────────
function Linea({ codigo, descripcion, valor, destacado, negativo, info, muted, sublinea, editable, onEditar }) {
  const [showInfo, setShowInfo] = useState(false);
  return (
    <div style={{
      display: "flex", alignItems: "center", justifyContent: "space-between",
      padding: sublinea ? "7px 14px 7px 28px" : "9px 14px",
      background: destacado ? "#f0fdf4" : "transparent",
      borderLeft: destacado ? "3px solid #10b981" : negativo ? "3px solid #dc2626" : "3px solid transparent",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flex: 1 }}>
        <span style={{
          fontSize: 11, fontWeight: 700, color: "#64748b",
          background: muted ? "transparent" : "#f1f5f9",
          borderRadius: 4, padding: "2px 6px", minWidth: 34, textAlign: "center", flexShrink: 0
        }}>{codigo}</span>
        <span style={{ fontSize: sublinea ? 12 : 13, color: muted ? "#94a3b8" : "#374151" }}>{descripcion}</span>
        {info && (
          <div style={{ position: "relative" }}>
            <Info size={12} color="#94a3b8" style={{ cursor: "pointer" }}
              onMouseEnter={() => setShowInfo(true)} onMouseLeave={() => setShowInfo(false)} />
            {showInfo && (
              <div style={{ position: "absolute", bottom: 20, left: 0, background: "#1e293b", color: "#f1f5f9", fontSize: 11, padding: "6px 10px", borderRadius: 6, whiteSpace: "nowrap", zIndex: 10 }}>{info}</div>
            )}
          </div>
        )}
      </div>
      {editable
        ? onEditar
        : (
          <span style={{
            fontSize: 13, fontWeight: destacado ? 700 : 500, fontVariantNumeric: "tabular-nums",
            color: muted ? "#cbd5e1" : negativo ? "#dc2626" : destacado ? "#059669" : "#0d1117"
          }}>
            {valor === null || valor === undefined ? "—" : money(valor)}
          </span>
        )
      }
    </div>
  );
}

function SubTitulo({ texto }) {
  return (
    <div style={{ padding: "6px 14px", background: "#f8fafc", borderTop: "1px solid #f1f5f9", borderBottom: "1px solid #f1f5f9" }}>
      <span style={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.5 }}>{texto}</span>
    </div>
  );
}

function Seccion({ titulo, icono, children, defaultOpen = true, badge }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ background: "#fff", borderRadius: 10, border: "1px solid #e2e8f0", overflow: "hidden", marginBottom: 12 }}>
      <div onClick={() => setOpen(!open)} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", cursor: "pointer", userSelect: "none", background: "#fafafa", borderBottom: open ? "1px solid #e2e8f0" : "none" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {icono}
          <span style={{ fontSize: 13, fontWeight: 700, color: "#0d1117" }}>{titulo}</span>
          {badge && <span style={{ fontSize: 11, background: "#e0f2fe", color: "#0369a1", borderRadius: 20, padding: "2px 8px", fontWeight: 600 }}>{badge}</span>}
        </div>
        {open ? <ChevronUp size={14} color="#94a3b8" /> : <ChevronDown size={14} color="#94a3b8" />}
      </div>
      {open && <div style={{ padding: "4px 0" }}>{children}</div>}
    </div>
  );
}

function Sep() { return <div style={{ height: 1, background: "#e2e8f0", margin: "2px 14px" }} />; }

// ── PRINCIPAL ────────────────────────────────────────────────
export default function F29() {
  const now = new Date();
  const [anio, setAnio]           = useState(now.getFullYear());
  const [mes, setMes]             = useState(now.getMonth() + 1);
  const [datos, setDatos]         = useState(null);
  const [loading, setLoading]     = useState(true);
  const [guardando, setGuardando] = useState(false);
const [guardado, setGuardado] = useState(false);
  const [error, setError]         = useState(null);
  const [ppmTasa, setPpmTasa]     = useState(1.0);
  const [remanInicial, setRemanInicial] = useState(0);
  const [empresaId, setEmpresaId] = useState(null);

  const periodo     = `${anio}-${String(mes).padStart(2, "0")}`;
  const vencimiento = new Date(anio, mes, 20);
  const diasVence   = Math.ceil((vencimiento - new Date()) / 86400000);

  // ── Cargar config empresa ────────────────────────────────────
  useEffect(() => {
    supabase.from("empresa").select("id, ppm_tasa, remanente_inicial").single()
      .then(({ data }) => {
        if (data) {
          setEmpresaId(data.id);
          setPpmTasa(data.ppm_tasa ?? 1.0);
          setRemanInicial(data.remanente_inicial ?? 0);
        }
      });
  }, []);

  // ── Guardar tasa PPM en empresa ──────────────────────────────
  const guardarPPM = async (v) => {
    setPpmTasa(v);
    if (empresaId) {
      await supabase.from("empresa").update({ ppm_tasa: v }).eq("id", empresaId);
    }
  };

  // ── Guardar remanente inicial en empresa ─────────────────────
  const guardarRemanente = async (v) => {
    setRemanInicial(v);
    if (empresaId) {
      await supabase.from("empresa").update({ remanente_inicial: v }).eq("id", empresaId);
    }
    // Recalcular con nuevo valor
    setTimeout(() => calcular(), 200);
  };

  // ── Calcular F29 ────────────────────────────────────────────
  const calcular = useCallback(async () => {
    setLoading(true);
    setError(null);
    setGuardado(false);
    try {
      const { data: ing } = await supabase
        .from("ingresos").select("monto_neto, iva, monto_total, tipo_dte")
        .eq("periodo", periodo).neq("estado", "anulada");

     const { data: gasConIva } = await supabase
  .from("gastos").select("monto_neto, iva_credito, monto_total, monto_exento")
  .eq("periodo", periodo).neq("estado", "anulado").gt("iva_credito", 0);

const { data: gasSinIva } = await supabase
  .from("gastos").select("monto_total, monto_exento")
  .eq("periodo", periodo).neq("estado", "anulado")
  .or("iva_credito.is.null,iva_credito.eq.0");

      const { data: hon } = await supabase
        .from("honorarios").select("monto_bruto, retencion").eq("periodo", periodo);

      const periodoAnt = mes === 1 ? `${anio-1}-12` : `${anio}-${String(mes-1).padStart(2,"0")}`;
      const { data: f29ant }   = await supabase.from("f29").select("remanente_iva").eq("periodo", periodoAnt).single();
      const { data: f29exist } = await supabase.from("f29").select("*").eq("periodo", periodo).single();
      // Si ya está declarado, mostrar datos guardados sin recalcular
if (f29exist?.estado === "declarado") {
  setDatos({
    periodo,
    ventasAfectas:    f29exist.ventas_afectas    || 0,
    ventas142:        f29exist.ventas_exentas    || 0,
    debitoFiscal:     f29exist.iva_debito        || 0,
    iva511:           f29exist.iva_credito       || 0,
    monto562:        f29exist.monto_562       || 0,  // ← corregir
    monto520:        f29exist.monto_520       || 0,  // ← corregir
    remanente504:      f29exist.remanente_504   || 0,  // ← corregir 
   totalCreditos537: (f29exist.iva_credito||0) + (f29exist.remanente_504||0),
    ivaDeterminado89: f29exist.iva_a_pagar       || 0,
    remanente77:      f29exist.remanente_iva     || 0,
    ppmBase:          f29exist.ppm_base          || 0,
    ppmTasa:          f29exist.ppm_tasa          || ppmTasa,
    ppm62:            f29exist.ppm_monto         || 0,
    ppm547:           f29exist.ppm_monto         || 0,
    totalHonorarios:  f29exist.honorarios_monto  || 0,
    retencionHon:     f29exist.retencion_hon     || 0,
    totalAPagar91:    f29exist.total_a_pagar     || 0,
    estado:           "declarado",
    yaGuardado:       true,
    hayF29Anterior:   true,
    fecha_declaracion: f29exist.fecha_declaracion,
    cantIngresos:     0,
    cantGastos:       0,
    cantHon:          0,
  });
  setLoading(false);
  return;  // salir sin seguir calculando
}

      // Config empresa (fresca)
      const { data: emp } = await supabase.from("empresa").select("ppm_tasa, remanente_inicial").single();
      const tasaActual    = emp?.ppm_tasa ?? ppmTasa;
      const remanInicialActual = emp?.remanente_inicial ?? remanInicial;

      // DÉBITOS
      const ventas142     = (ing||[]).filter(i=>i.tipo_dte==="34").reduce((s,i)=>s+(i.monto_total||0),0);
      const ventasAfectas = (ing||[]).filter(i=>i.tipo_dte!=="34").reduce((s,i)=>s+(i.monto_neto||0),0);
      const debitoFiscal  = Math.round(ventasAfectas * 0.19);

      // CRÉDITOS
     const iva511   = (gasConIva||[]).reduce((s,g)=>s+(g.iva_credito||0),0);
const monto562 = (gasSinIva||[]).reduce((s,g)=>s+(g.monto_total||0),0);
      const monto520 = iva511;

      // 504: si hay F29 del mes anterior usar su remanente, sino usar remanente_inicial
      const remanente504 = f29ant?.remanente_iva != null
        ? f29ant.remanente_iva
        : remanInicialActual;

      const hayF29Anterior = f29ant?.remanente_iva != null;

      const totalCreditos537 = iva511 + remanente504;
      const ivaDeterminado89 = Math.max(0, debitoFiscal - totalCreditos537);
      const remanente77      = totalCreditos537 > debitoFiscal ? totalCreditos537 - debitoFiscal : 0;

      const ppmBase = ventasAfectas + ventas142;
      const ppm62   = Math.round(ppmBase * (tasaActual / 100));
      const ppm547  = ppm62;

      const totalHonorarios = (hon||[]).reduce((s,h)=>s+(h.monto_bruto||0),0);
      const retencionHon    = (hon||[]).reduce((s,h)=>s+(h.retencion||0),0);
      const totalAPagar91   = ivaDeterminado89 + ppm547 + retencionHon;

      setPpmTasa(tasaActual);
      setRemanInicial(remanInicialActual);

      setDatos({
        periodo, ventasAfectas, ventas142, debitoFiscal,
        iva511, monto562, monto520, remanente504, totalCreditos537,
        ivaDeterminado89, remanente77,
        ppmBase, ppmTasa: tasaActual, ppm62, ppm547,
        totalHonorarios, retencionHon, totalAPagar91,
      estado: f29exist?.estado || "borrador",
yaGuardado: !!f29exist,
guardadoLocal: !!f29exist,
        hayF29Anterior,
        cantIngresos: (ing||[]).length,
        cantGastos: ((gasConIva||[]).length + (gasSinIva||[]).length),
        cantHon: (hon||[]).length,
      });
    } catch (e) {
      console.error("F29:", e);
      setError("Error al calcular: " + e.message);
      setDatos({
        periodo, ventasAfectas:0, ventas142:0, debitoFiscal:0,
        iva511:0, monto562:0, monto520:0, remanente504:remanInicial, totalCreditos537:0,
        ivaDeterminado89:0, remanente77:0,
        ppmBase:0, ppmTasa, ppm62:0, ppm547:0,
        totalHonorarios:0, retencionHon:0, totalAPagar91:0,
        estado:"borrador", yaGuardado:false, hayF29Anterior:false,
        cantIngresos:0, cantGastos:0, cantHon:0,
      });
    } finally { setLoading(false); }
  }, [periodo, ppmTasa, remanInicial]);

  useEffect(() => { calcular(); }, [calcular]);

  const guardar = async () => {
    if (!datos) return;
    setGuardando(true);
    try {
      const { error: e } = await supabase.from("f29").upsert({
        periodo,
        ventas_afectas: datos.ventasAfectas, ventas_exentas: datos.ventas142,
        iva_debito: datos.debitoFiscal, iva_credito: datos.iva511,
        iva_a_pagar: datos.ivaDeterminado89, remanente_iva: datos.remanente77,
        ppm_base: datos.ppmBase, ppm_tasa: datos.ppmTasa, ppm_monto: datos.ppm62,
          remanente_504:    datos.remanente504,   // ← AGREGAR
  monto_562:        datos.monto562,       // ← AGREGAR
  monto_520:        datos.monto520,       // ← AGREGAR
        honorarios_monto: datos.totalHonorarios, retencion_hon: datos.retencionHon,
        total_a_pagar: datos.totalAPagar91, estado: "borrador",
        updated_at: new Date().toISOString(),
      }, { onConflict: "periodo" });
      if (e) throw e;
     // setGuardado(true);
      setDatos(d => ({ ...d, yaGuardado: true }));
    } catch (e) { setError("Error al guardar: " + e.message); }
    finally { setGuardando(false); }
  };

  const marcarDeclarado = async () => {
    if (!datos?.yaGuardado) return;
    await supabase.from("f29")
      .update({ estado: "declarado", fecha_declaracion: new Date().toISOString() })
      .eq("periodo", periodo);
    setDatos(d => ({ ...d, estado: "declarado" }));
  };

  const mesAnterior  = () => { if(mes===1){setMes(12);setAnio(a=>a-1);}else setMes(m=>m-1); };
  const mesSiguiente = () => { if(mes===12){setMes(1);setAnio(a=>a+1);}else setMes(m=>m+1); };

  return (
    <div style={{ padding: "28px 32px", fontFamily: "inherit" }}>

      {/* HEADER */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 800, color: "#0d1117", marginBottom: 3 }}>Formulario F29</h1>
          <p style={{ fontSize: 12, color: "#64748b" }}>Declaración mensual SII · IVA · PPM · Retenciones</p>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <button onClick={mesAnterior} style={btnNav}>‹</button>
          <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, padding: "8px 16px", textAlign: "center", minWidth: 130 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#0d1117" }}>{MESES[mes-1]} {anio}</div>
            <div style={{ fontSize: 10, color: "#94a3b8" }}>Período tributario</div>
          </div>
          <button onClick={mesSiguiente} style={btnNav}>›</button>
        </div>
      </div>

      {/* CONFIG RÁPIDA */}
      <div style={{ display: "flex", gap: 12, marginBottom: 20, flexWrap: "wrap" }}>
        {/* Tasa PPM */}
        <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, padding: "12px 16px", display: "flex", alignItems: "center", gap: 12 }}>
          <div>
            <div style={{ fontSize: 11, color: "#94a3b8", marginBottom: 2 }}>Tasa PPM (empresa)</div>
            <CampoEditable
              valor={ppmTasa}
              tipo="pct"
              sufijo="%"
              label="tasa PPM"
              onGuardar={guardarPPM}
            />
          </div>
          <div style={{ fontSize: 11, color: "#94a3b8", maxWidth: 160, lineHeight: 1.4 }}>
            Se aplica a todos los períodos. Consúltala en Mis Declaraciones del SII.
          </div>
        </div>

        {/* Remanente inicial */}
        {datos && !datos.hayF29Anterior && (
          <div style={{ background: "#fffbeb", border: "1px solid #f59e0b", borderRadius: 10, padding: "12px 16px", display: "flex", alignItems: "center", gap: 12 }}>
            <div>
              <div style={{ fontSize: 11, color: "#92400e", marginBottom: 2, fontWeight: 600 }}>
                ⚠️ Remanente inicial (línea 504)
              </div>
              <CampoEditable
                valor={remanInicial}
                tipo="monto"
                prefijo="$"
                label="remanente inicial"
                onGuardar={guardarRemanente}
              />
            </div>
            <div style={{ fontSize: 11, color: "#92400e", maxWidth: 200, lineHeight: 1.4 }}>
              No hay F29 anterior en el sistema. Ingresa el remanente del mes previo desde tu declaración SII.
            </div>
          </div>
        )}
      </div>

      {/* ALERTAS */}
      {diasVence <= 10 && diasVence > 0 && (
        <div style={{ background: "#fef3c7", border: "1px solid #f59e0b", borderRadius: 8, padding: "10px 14px", marginBottom: 16, display: "flex", gap: 8, alignItems: "center" }}>
          <AlertTriangle size={15} color="#d97706" />
          <span style={{ fontSize: 13, color: "#92400e", fontWeight: 600 }}>
            Vence en {diasVence} día{diasVence>1?"s":""} — 20 de {MESES[mes]} {mes===12?anio+1:anio}
          </span>
        </div>
      )}
      {diasVence <= 0 && (
        <div style={{ background: "#fef2f2", border: "1px solid #dc2626", borderRadius: 8, padding: "10px 14px", marginBottom: 16, display: "flex", gap: 8, alignItems: "center" }}>
          <AlertTriangle size={15} color="#dc2626" />
          <span style={{ fontSize: 13, color: "#991b1b", fontWeight: 600 }}>El plazo para este período ya venció.</span>
        </div>
      )}
      {error && <div style={{ background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: 8, padding: "10px 14px", marginBottom: 14, fontSize: 13, color: "#991b1b" }}>{error}</div>}

      {loading ? (
        <div style={{ textAlign: "center", padding: 60, color: "#94a3b8" }}>
          <RefreshCw size={26} style={{ animation: "spin 1s linear infinite" }} />
          <p style={{ marginTop: 10, fontSize: 13 }}>Calculando {MESES[mes-1]} {anio}…</p>
        </div>
      ) : datos ? (
        <>
          {/* KPIs */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 10, marginBottom: 20 }}>
            {[
              { label: "Débito fiscal", valor: datos.debitoFiscal, color: "#f59e0b" },
              { label: "Total créditos (537)", valor: datos.totalCreditos537, color: "#29abe2" },
              { label: "IVA determinado (89)", valor: datos.ivaDeterminado89, color: datos.ivaDeterminado89>0?"#dc2626":"#10b981" },
              { label: "Total a pagar (91)", valor: datos.totalAPagar91, color: datos.totalAPagar91>0?"#dc2626":"#10b981" },
            ].map(k => (
              <div key={k.label} style={{ background: "#fff", borderRadius: 8, border: "1px solid #e2e8f0", padding: "12px 14px" }}>
                <div style={{ fontSize: 11, color: "#94a3b8", marginBottom: 4 }}>{k.label}</div>
                <div style={{ fontSize: 15, fontWeight: 700, color: k.color }}>{money(k.valor)}</div>
              </div>
            ))}
          </div>

          {/* BADGE ESTADO */}
          <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", background: "#f8fafc", borderRadius: 8, border: "1px solid #e2e8f0", marginBottom: 16, flexWrap: "wrap" }}>
            <span style={{
              fontSize: 11, fontWeight: 700, borderRadius: 20, padding: "3px 10px",
              background: datos.estado==="declarado"?"#d1fae5":datos.yaGuardado?"#e0f2fe":"#f3f4f6",
              color: datos.estado==="declarado"?"#065f46":datos.yaGuardado?"#0369a1":"#6b7280"
            }}>
              {datos.estado==="declarado"?"✓ Declarado":datos.yaGuardado?"Borrador guardado":"Sin guardar"}
            </span>
            <span style={{ fontSize: 12, color: "#64748b" }}>
              {datos.cantIngresos} ingreso{datos.cantIngresos!==1?"s":""} · {datos.cantGastos} gasto{datos.cantGastos!==1?"s":""} · {datos.cantHon} honorario{datos.cantHon!==1?"s":""}
            </span>
            <button onClick={calcular} style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 5, padding: "5px 10px", border: "1px solid #e2e8f0", borderRadius: 6, background: "#fff", fontSize: 12, cursor: "pointer", color: "#374151" }}>
              <RefreshCw size={11} /> Recalcular
            </button>
          </div>

          {/* DÉBITOS */}
          <Seccion titulo="Débitos y Ventas" icono={<TrendingUp size={15} color="#10b981" />} badge={`${datos.cantIngresos} docs`}>
            <SubTitulo texto="Información de Ingresos" />
            <Linea codigo="142" descripcion="Ventas y/o servicios exentos o no gravados del giro" valor={datos.ventas142} info="Facturas tipo 34" />
            {datos.ventasAfectas > 0 && <Linea codigo="503" descripcion="Ventas afectas — base imponible IVA" valor={datos.ventasAfectas} />}
            {datos.debitoFiscal > 0 && (<><Sep/><Linea codigo="502" descripcion="Débito fiscal IVA (19% sobre ventas afectas)" valor={datos.debitoFiscal} destacado /></>)}
          </Seccion>

          {/* CRÉDITOS */}
          <Seccion titulo="Créditos y Compras" icono={<DollarSign size={15} color="#29abe2" />} badge={`${datos.cantGastos} docs`}>
            <SubTitulo texto="IVA por documentos electrónicos recibidos" />
            <Linea codigo="511" descripcion="Con derecho a crédito" valor={datos.iva511} info="IVA de facturas de compra DTE recibidos" />
            {datos.monto562 > 0 && <Linea codigo="562" descripcion="Sin derecho a crédito — internas exentas o no gravadas" valor={datos.monto562} />}
            <SubTitulo texto="Con derecho a crédito fiscal" />
            <Linea codigo="520" descripcion="Facturas recibidas del giro y facturas de compra emitidas" valor={datos.monto520} sublinea />

            {/* 504 editable si no hay F29 anterior */}
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              padding: "7px 14px 7px 28px",
              background: !datos.hayF29Anterior ? "#fffbeb" : "transparent",
              borderLeft: "3px solid transparent"
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: "#64748b", background: "#f1f5f9", borderRadius: 4, padding: "2px 6px", minWidth: 34, textAlign: "center" }}>504</span>
                <span style={{ fontSize: 12, color: "#374151" }}>Remanente crédito fiscal mes anterior</span>
                {!datos.hayF29Anterior && (
                  <span style={{ fontSize: 10, background: "#fef3c7", color: "#92400e", borderRadius: 4, padding: "1px 6px", fontWeight: 600 }}>Ingresado manualmente</span>
                )}
              </div>
              {!datos.hayF29Anterior
                ? <CampoEditable valor={remanInicial} tipo="monto" label="remanente 504" onGuardar={guardarRemanente} />
                : <span style={{ fontSize: 13, fontWeight: 500, color: "#0d1117", fontVariantNumeric: "tabular-nums" }}>{money(datos.remanente504)}</span>
              }
            </div>

            <Sep/>
            <Linea codigo="537" descripcion="Total créditos" valor={datos.totalCreditos537} destacado />
          </Seccion>

          {/* IMPUESTO */}
          <Seccion titulo="Impuesto determinado" icono={<FileText size={15} color="#f59e0b" />}>
            {datos.remanente77 > 0 ? (
              <Linea codigo="77" descripcion="Remanente crédito fiscal para período siguiente" valor={datos.remanente77} info="Se arrastrará como línea 504 del mes siguiente automáticamente" />
            ) : (
              <Linea codigo="89" descripcion="IVA determinado (débito − crédito)" valor={datos.ivaDeterminado89}
                destacado={datos.ivaDeterminado89===0} negativo={datos.ivaDeterminado89>0} />
            )}
          </Seccion>

          {/* PPM */}
          <Seccion titulo="PPM — Pagos Provisionales Mensuales" icono={<TrendingUp size={15} color="#8b5cf6" />}>
            <Linea codigo="—" descripcion="Base PPM (ingresos brutos del período)" valor={datos.ppmBase} muted />
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "9px 14px", borderLeft: "3px solid transparent" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: "#64748b", background: "#f1f5f9", borderRadius: 4, padding: "2px 6px", minWidth: 34, textAlign: "center" }}>62</span>
                <span style={{ fontSize: 13, color: "#374151" }}>PPM neto determinado</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ fontSize: 12, color: "#94a3b8" }}>tasa:</span>
                <CampoEditable valor={ppmTasa} tipo="pct" label="tasa PPM" onGuardar={guardarPPM} />
                <span style={{ fontSize: 13, fontWeight: 700, color: "#059669", fontVariantNumeric: "tabular-nums" }}>{money(datos.ppm62)}</span>
              </div>
            </div>
            <Sep/>
            <Linea codigo="547" descripcion="Total determinado" valor={datos.ppm547} destacado />
          </Seccion>

          {/* HONORARIOS */}
          {datos.totalHonorarios > 0 && (
            <Seccion titulo="Retenciones honorarios" icono={<ClipboardCheck size={15} color="#ec4899" />} badge={`${datos.cantHon} boletas`}>
              <Linea codigo="166" descripcion="Total honorarios pagados" valor={datos.totalHonorarios} />
              <Linea codigo="167" descripcion="Retención 10.75%" valor={datos.retencionHon} destacado />
            </Seccion>
          )}

          {/* TOTAL */}
          <div style={{ background: datos.totalAPagar91>0?"#fef2f2":"#f0fdf4", border: `1px solid ${datos.totalAPagar91>0?"#fca5a5":"#6ee7b7"}`, borderRadius: 12, padding: "20px 24px", marginBottom: 18 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#374151", marginBottom: 14 }}>Resumen F29 — {MESES[mes-1]} {anio}</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 16 }}>
              {[
                { label: "IVA a pagar (89)", valor: datos.ivaDeterminado89 },
                { label: "PPM (547)", valor: datos.ppm547 },
                { label: "Retenciones (167)", valor: datos.retencionHon },
              ].map(r => (
                <div key={r.label} style={{ background: "#fff", borderRadius: 8, padding: "10px 12px", border: "1px solid #e2e8f0" }}>
                  <div style={{ fontSize: 11, color: "#64748b", marginBottom: 3 }}>{r.label}</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "#0d1117" }}>{money(r.valor)}</div>
                </div>
              ))}
            </div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", paddingTop: 14, borderTop: "1px solid #e2e8f0" }}>
              <div>
                <div style={{ fontSize: 11, color: "#64748b", marginBottom: 2 }}>Línea 91 — Total a pagar en plazo legal</div>
                <div style={{ fontSize: 28, fontWeight: 900, color: datos.totalAPagar91>0?"#dc2626":"#059669" }}>{money(datos.totalAPagar91)}</div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 5, color: "#94a3b8", fontSize: 12 }}>
                <Clock size={13} /> Vence 20 de {MESES[mes]} {mes===12?anio+1:anio}
              </div>
            </div>
          </div>

          {/* ACCIONES */}
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button onClick={guardar} disabled={guardando||datos.estado==="declarado"} style={{ display:"flex",alignItems:"center",gap:6,padding:"10px 18px",border:"none",borderRadius:8,background:guardado?"#10b981":"#29abe2",color:"#fff",fontSize:13,fontWeight:700,cursor:datos.estado==="declarado"?"not-allowed":"pointer",opacity:datos.estado==="declarado"?0.6:1,fontFamily:"inherit" }}>
              {guardando?<RefreshCw size={13} style={{animation:"spin 1s linear infinite"}}/>:guardado?<CheckCircle size={13}/>:<FileText size={13}/>}
             {datos.estado==="declarado"?"✓ Declarado":guardado?"Guardado ✓":datos.yaGuardado?"Actualizar borrador":"Guardar borrador"}
            </button>
            {datos.yaGuardado && datos.estado!=="declarado" && (
              <button onClick={marcarDeclarado} style={{ display:"flex",alignItems:"center",gap:6,padding:"10px 18px",border:"none",borderRadius:8,background:"#059669",color:"#fff",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit" }}>
                <CheckCircle size={13}/> Marcar como declarado
              </button>
            )}
            <button onClick={()=>window.print()} style={{ display:"flex",alignItems:"center",gap:6,padding:"10px 18px",border:"1px solid #e2e8f0",borderRadius:8,background:"#fff",color:"#374151",fontSize:13,cursor:"pointer",fontFamily:"inherit" }}>
              <Printer size={13}/> Imprimir
            </button>
          </div>

          {datos.remanente77 > 0 && (
            <div style={{ marginTop: 14, padding: "10px 14px", background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 8, fontSize: 13, color: "#1e40af" }}>
              💡 Remanente <strong>{money(datos.remanente77)}</strong> (línea 77) — se cargará automáticamente como línea 504 del F29 de {MESES[mes]} {mes===12?anio+1:anio}.
            </div>
          )}
        </>
      ) : null}

      <style>{`
        @keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}
        @media print{button{display:none!important}}
      `}</style>
    </div>
  );
}

const btnNav = { width:28,height:28,border:"1px solid #e2e8f0",borderRadius:6,background:"#fff",cursor:"pointer",fontSize:16,color:"#374151",display:"flex",alignItems:"center",justifyContent:"center" };
